// Journey detection for the registration form selection (middleware form-selection spec).
//
// The middleware decides which "create account" form a NEW user sees after login by
// inferring the journey from the inbound URL (no dedicated query param is added by
// checkout/activation). See docs/middleware-form-selection.md (Part B).
//
// Detection rules (match on the parsed PATH SEGMENT, never a naive substring, so a
// query-string value that merely contains the word is not a false positive):
//   - Checkout   : ReturnUrl path starts with /payment  OR  PassThruUrl path starts with /guest
//   - Activation : ReturnUrl path has an /activation segment
//
// Activation can also be signalled explicitly via the login path /{env}/login/activation
// (LoginAuth persists sessionStorage 'journey'='activation'; Register reads it). The
// ReturnUrl '/activation' path-segment check below is an additional/fallback signal.

const CHECKOUT_PATH_PREFIXES = ['payment'];
const GUEST_PATH_PREFIXES = ['guest'];
const ACTIVATION_SEGMENT = 'activation';

// Safely parse a URL string into its lowercased path segments.
// Accepts absolute URLs (https://host/payment/123) and relative paths (/payment/123).
const getPathSegments = (urlString) => {
    if (!urlString) return [];
    try {
        // Provide a base so relative paths parse too.
        const url = new URL(urlString, window.location.origin);
        return url.pathname
            .split('/')
            .filter((segment) => segment.length > 0)
            .map((segment) => segment.toLowerCase());
    } catch (e) {
        // Not a parseable URL - treat as "no journey".
        return [];
    }
};

// Reads a query param off the current browser location (defaults) or a provided search string.
const getParam = (name, search) => {
    const params = new URLSearchParams(search ?? window.location.search);
    return params.get(name);
};

// True when the given URL targets the guest-checkout payment route.
const isCheckoutUrl = (returnUrl, passThruUrl) => {
    const returnSegments = getPathSegments(returnUrl);
    const passThruSegments = getPathSegments(passThruUrl);

    const firstReturn = returnSegments[0];
    const firstPassThru = passThruSegments[0];

    const returnIsPayment = firstReturn !== undefined && CHECKOUT_PATH_PREFIXES.includes(firstReturn);
    const passThruIsGuest = firstPassThru !== undefined && GUEST_PATH_PREFIXES.includes(firstPassThru);

    return returnIsPayment || passThruIsGuest;
};

// True when the given URL targets the corporate-activation route.
const isActivationUrl = (returnUrl) => {
    const segments = getPathSegments(returnUrl);
    return segments.includes(ACTIVATION_SEGMENT);
};

// Convenience wrappers that read ReturnUrl / PassThruUrl from the current location.
const isCheckoutJourney = (search) => {
    const returnUrl = getParam('ReturnUrl', search) || getParam('returl', search);
    const passThruUrl = getParam('PassThruUrl', search);
    return isCheckoutUrl(returnUrl, passThruUrl);
};

const isActivationJourney = (search) => {
    const returnUrl = getParam('ReturnUrl', search) || getParam('returl', search);
    return isActivationUrl(returnUrl);
};

export {
    isCheckoutUrl,
    isActivationUrl,
    isCheckoutJourney,
    isActivationJourney,
    getPathSegments,
};
